/* @flow */
import { getterFromKeyPath, alphabetize, capitalize } from 'trustar/utils';
import { createSelector } from 'reselect';
import { Map, List, OrderedMap } from 'immutable';
import type { Enclave } from 'modules/enclave/types';
import { prop, tryCatch, pipe } from 'ramda';

import { enclavesMap } from 'modules/enclave/getters';
import { getEnclaveGroup } from 'modules/enclave/types';
import { isCommunityEnclave } from 'modules/enclave/utils';
import { flags as Flags, getters as FeatureGetters } from 'modules/features';
import type { Role, Permission } from 'modules/roles';

import { typeof Profile } from './types.js';

export const profile = getterFromKeyPath(['profile', 'profile']);
export const companyQuotas = getterFromKeyPath(['profile', 'quotas']);
export const defaultEnclave = getterFromKeyPath(['profile', 'profile', 'preferences', 'defaultEnclaveGuid'], null);
export const loading = getterFromKeyPath(['profile', 'loading']);
export const seenUpsellModal = getterFromKeyPath(['profile', 'seenUpsellModal']);

export const profileGuid = createSelector(profile, prop('guid'));
export const profileEmail = createSelector(profile, prop('email'));
import { COMMUNITY_ENCLAVE } from 'modules/enclave/types';

export const filter = (type: string, id: string) => {
  return [
    profile,
    (profile: Profile) => profile.getIn([`${type}FilterSets`, id]),
  ];
};

export const role = createSelector(
  profile,
  (profile: Profile) => profile.get('role')
);

export const roleName = createSelector(
  role,
  (role: Role) => role && role.get('name')
);

export const permissions = createSelector(
  role,
  (role: Role) => role && role.get('permissions')
);

export const hasPermission = (permission: Permission) => createSelector(
  permissions,
  (permissions: List<Permission>) => permissions && permissions.contains(permission)
);

export const adminRole = createSelector(
  role,
  (role: Role) => role && (role.get('internal') ? role.get('displayName') : null)
);

/**
 * This could perhaps more accurately be called "isTrialCompany" but since the
 * information is coming from profile I think this makes more sense, especially
 * to anyone reading the code outside of this file. I.e.
 * Profile.getters.isTrialUser.
 */
export const isTrialUser = createSelector(
  profile,
  (profile: Profile) => profile.getIn(['company', 'trialCompany'], false)
);

export const company = createSelector(
  profile,
  (profile: Profile) => profile.get('company'),
);

export const companyId = createSelector(
  company,
  x => x ? x.get('guid') : null
);


export const companyQuotaExceeded = createSelector(
  companyQuotas,
  tryCatch(
    pipe(
      quotas => quotas.first(),
      quota => {
        return quota.get('usedRequests') >= quota.get('maxRequests');
      }
    ),
    () => false
  )
);

/**
 * User has NOT seen the modal yet and the quota is exceeded. Whether or not the
 * quota has been exceeded is a simple projection of quota state, however, we
 * also need to track whether or not the user has seen it in order to hide the
 * modal.
 */
export const showUpsellModal = createSelector(
  [seenUpsellModal, companyQuotaExceeded],
  (hasSeen, isExceeded) => {
    return !hasSeen && isExceeded;
  },
);

export const enclaves = createSelector(
  [ profile, enclavesMap, hasPermission('get_data'), FeatureGetters.isFeatureEnabled(Flags.COMMUNITY_ENCLAVE_ENABLED) ],
  (profile: Map<string, any>, enclavesMap: any, canGetEnclaves: boolean, isCommunityEnclaveEnabled: boolean) => {
    if (canGetEnclaves) return enclavesMap;

    return List(profile.get('enclaves', List()))
      .map(x => {
        const color = enclavesMap.getIn([x.get('id'), 'color']);
        return x.set('color', color);
      })
      .filter(enclave => {
        const isCommunityEnclaveFlag = isCommunityEnclave(enclave);

        if (isCommunityEnclaveFlag) {
          return isCommunityEnclaveEnabled;
        } else {
          return enclave.get('read', false);
        }
      })
      .toOrderedMap()
      .mapKeys((k, v) => v.get('id'))
      .sort(alphabetize);
  },
);

export const enclaveList = createSelector(
  enclaves,
  (enclaves: OrderedMap<string, Enclave>): ?List<Object> => enclaves.toList(),
);

export const privateEnclaves = createSelector(
  enclaves,
  // TODO
  // enclaves => enclaves.filter(isPrivateEnclave)
  enclaves => enclaves.filter(enc => enc.label == 'INTERNAL')
);

export const privateEnclavesIdSet = createSelector(
  enclaves,
  enclaves => enclaves.filter(enc => enc.label == 'INTERNAL')
    .map(enc => enc.get('id'))
    .toSet()
);

export const submissionEnclaves = createSelector(
  [ profile, enclavesMap ],
  (profile: Profile, enclavesMap: any) => {
    return List(profile.get('enclaves', List()))
    .filter(x => x.get('create') && x.get('label') !== 'COMMUNITY')
    .map(x => {
      const color = enclavesMap.getIn([x.get('id'), 'color']);
      x = x.set('color', color);
      return x;
    })
    .toOrderedMap()
    .mapKeys((k, v) => v.get('id'));
  },
);

/* Get select options for submission workflow enclaves */
export const submissionEnclaveOptions = createSelector(
  [submissionEnclaves, FeatureGetters.isFeatureEnabled(Flags.COMMUNITY_ENCLAVE_ENABLED)],
  (enclaves: ?OrderedMap<string, Enclave>, isCommunityEnclaveEnabled: boolean): ?Array<Object> => {
    if (enclaves) {
      const enclaveList = isCommunityEnclaveEnabled
        ? enclaves.toList().push(COMMUNITY_ENCLAVE)
        : enclaves.toList();

      return enclaveList
        .map(enclave => ({
          value: enclave.get('id'),
          label: capitalize(enclave.get('name')),
        }))
        .toJS();
    }
  }
);

export const updateEnclaves = createSelector(
  [ profile, enclavesMap ],
  (profile: Profile, enclavesMap: any) => {
    return List(profile.get('enclaves', List()))
    .filter(x => x.get('update') && x.get('label') !== 'COMMUNITY')
    .map(x => {
      const color = enclavesMap.getIn([x.get('id'), 'color']);
      x = x.set('color', color);
      return x;
    })
    .toOrderedMap()
    .mapKeys((k, v) => v.get('id'));
  },
);

/* Get select options for update workflow enclaves */
export const updateEnclaveOptions = createSelector(
  [updateEnclaves, FeatureGetters.isFeatureEnabled(Flags.COMMUNITY_ENCLAVE_ENABLED)],
  (enclaves: ?OrderedMap<string, Enclave>, isCommunityEnclaveEnabled: boolean): ?Array<Object> => {
    if (enclaves) {
      const enclaveList = isCommunityEnclaveEnabled
        ? enclaves.toList().push(COMMUNITY_ENCLAVE)
        : enclaves.toList();

      return enclaveList
        .map(enclave => ({
          value: enclave.get('id'),
          label: capitalize(enclave.get('name')),
        }))
        .toJS();
    }
  }
);

export const hasEnclaves = createSelector(
  profile,
  (profile: Profile) => {
    return !!List(profile.get('enclaves', List())).size;
  }
);

export const hasPrivateEnclaves = createSelector(
  privateEnclaves,
  (privateEnclaves: Map<string, any>) => {
    return !!List(privateEnclaves).size;
  }
);

export const sector = createSelector(
  profile,
  (profile: Profile) => profile.getIn(['company', 'sector']),
);

export const isAccountActivationRecent = createSelector(
  profile,
  (profile: Profile): boolean => {
    const accountActivationTime = profile.get('activationDate');
    const timeNow = new Date();
    const accountAge = timeNow - accountActivationTime;
    const oneWeek = 1000 * 60 * 60 * 24 * 7;
    return accountAge < oneWeek;
  },
);

/**
 * NOTE: Group enclaves by their "type" as decided by getEnclaveGroup
 */
export const groupedFilterEnclaves = createSelector(
  enclaves,
  (enclaves: OrderedMap<any, any>) => {
    const groups = enclaves.toList().groupBy(getEnclaveGroup);
    // This lovely bit of logic is to manually ensure the ordering of private,
    // closed source and osint lists
    return OrderedMap({
      COMMUNITY: groups.get('COMMUNITY', List()),
      INTERNAL: groups.get('INTERNAL', List()),
      CLOSED_CONCRETE: groups.get('CLOSED_CONCRETE', List()),
      OPEN: groups.get('OPEN', List()),
    })
    .filter(x => x.size); // Only take where there are enclaves present
  }
);

// Determine if profile has been fetched
export const profileLoadComplete = createSelector(
  profile,
  (profile: Profile) => !!profile.get(['guid']) && !!profile.get(['email']),
);



// WEBPACK FOOTER //
// ./src/app/modules/profile/getters.js